import { Link } from '@tanstack/react-router';
import {
  HeroCards as SharedHeroCards,
  type HeroCardDefinition,
  type HeroCardsLinkComponentProps,
} from '@org/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RouterOutputs } from '@org/clients/school';
import { trpc } from '@org/clients/school';
import { getJoinState, extractFirstUrl } from './join-utils';

type HeroStats = RouterOutputs['dashboard']['heroStats'];

type HeroCardsProps = {
  heroStats?: HeroStats | null;
  isLoading: boolean;
};

export function HeroCards({ heroStats, isLoading }: HeroCardsProps) {
  const { t } = useTranslation('admin');
  const { data: feedbackList } = trpc.dashboard.studentFeedback.list.useQuery();
  const latestFeedback = feedbackList?.[0] ?? null;
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [],
  );

  const formatDate = (iso?: string | null) => (iso ? dateFormatter.format(new Date(iso)) : undefined);

  const liveLabel = t('p2e.dashboard.stats.liveLabel', { defaultValue: 'Live' });
  const soonLabel = t('p2e.dashboard.stats.soonLabel', { defaultValue: 'Starting soon' });

  // The second hero card shows whatever comes next chronologically for this
  // student — a class or a community event (server picks and tie-breaks).
  const nextUp = heroStats?.nextUp ?? null;

  const nextUpJoin = useMemo(() => {
    const href = extractFirstUrl(nextUp?.location ?? null);
    const state = getJoinState(nextUp?.startTime ?? null, nextUp?.endTime ?? null);
    return href && state ? { href, state } : null;
  }, [nextUp?.location, nextUp?.startTime, nextUp?.endTime]);

  const heroCards = useMemo<HeroCardDefinition[]>(
    () => [
      {
        key: 'lastClass',
        title: t('p2e.dashboard.stats.lastClass.title'),
        value:
          (heroStats?.lastClass?.startTime && formatDate(heroStats.lastClass.startTime)) ??
          t('p2e.dashboard.stats.lastClass.emptyValue'),
        description:
          heroStats?.lastClass?.className ??
          heroStats?.lastClass?.eventTitle ??
          t('p2e.dashboard.stats.lastClass.emptyDescription'),
        href: heroStats?.lastClass?.classId
          ? { to: '/classes/$classId', params: { classId: heroStats.lastClass.classId } }
          : undefined,
        meta: heroStats?.lastClass
          ? {
              statusLabel: t('p2e.dashboard.stats.lastClass.statusLabel'),
              statusValue: heroStats.lastClass.status
                ? t(`p2e.dashboard.stats.lastClass.status.${heroStats.lastClass.status}`)
                : t('p2e.dashboard.stats.lastClass.status.pending'),
              detail: t('p2e.dashboard.stats.lastClass.progressDetail', {
                    percent: heroStats.lastClass.completionPercent ?? 0,
                  }),
            }
          : undefined,
      },
      {
        key: 'nextClass',
        // Community events get their own label (p2e namespace); classes keep
        // the legacy admin-namespace key used across the dashboard.
        title:
          nextUp?.kind === 'community'
            ? t('p2e:dashboard.stats.nextEvent.title')
            : t('p2e.dashboard.stats.nextClass.title'),
        value:
          (nextUp?.startTime && formatDate(nextUp.startTime)) ??
          t('p2e.dashboard.stats.nextClass.emptyValue'),
        description: nextUp
          ? nextUp.className ?? nextUp.eventTitle ?? undefined
          : t('p2e.dashboard.stats.nextClass.emptyDescription'),
        // Class-linked items go to the lesson player; other community events
        // land on the student calendar where the detail card lives.
        href: nextUp?.classId
          ? { to: '/classes/$classId', params: { classId: nextUp.classId } }
          : nextUp?.kind === 'community'
            ? { to: '/calendar' }
            : undefined,
        join: nextUpJoin
          ? {
              href: nextUpJoin.href,
              state: nextUpJoin.state,
              stateLabel: nextUpJoin.state === 'live' ? liveLabel : soonLabel,
            }
          : undefined,
      },
      {
        key: 'feedback',
        title: t('p2e.dashboard.stats.feedback.title'),
        value: latestFeedback
          ? latestFeedback.teacherName
          : t('p2e.dashboard.stats.feedback.emptyValue'),
        description: latestFeedback?.publishedAt
          ? t('p2e.dashboard.stats.feedback.publishedOn', {
              date: new Date(latestFeedback.publishedAt).toLocaleDateString(),
            })
          : t('p2e.dashboard.stats.feedback.emptyDescription'),
        href: latestFeedback ? { to: '/feedback' } : undefined,
      },
    ],
    [heroStats, latestFeedback, liveLabel, nextUp, nextUpJoin, soonLabel, t],
  );

  const isHeroCardsLoading = isLoading && !heroStats;

  const RouterLink = ({ to, params, className, children }: HeroCardsLinkComponentProps) => (
    <Link to={to} params={params} className={className}>
      {children}
    </Link>
  );

  return (
    <SharedHeroCards
      cards={heroCards}
      isLoading={isHeroCardsLoading}
      loadingCount={heroCards.length}
      LinkComponent={RouterLink}
    />
  );
}
